/**
 * Plain blows, the guard, casting and being hit: their key poses, the motions between them and
 * the straight-limb arms they swing the weapon through.
 */
import type { MotionDef, LimbSpec, PoseRigs } from './kit';

/** This group's poses. */
export type Poses =
  | 'atk'
  | 'block'
  | 'cast'
  | 'windup'
  | 'cut'
  | 'follow'
  | 'lowGuard'
  | 'thrust'
  | 'overhead'
  | 'chest'
  | 'blockHit'
  | 'hurt'
  // Breakdowns (lane C0-WEAPON): one between each pair of a strike's keys, so the blade drags behind the hand on
  // its arc and the body leads the blow (overlap) instead of every joint sliding straight from key to key. One
  // between each pair keeps every old key at the same fraction of its motion, as the renderer counts it.
  | 'cleaveOver'
  | 'cleaveDrive'
  | 'cleaveThrough'
  | 'thrustChamber'
  | 'thrustRecoil'
  | 'risingSweep'
  | 'risingHigh'
  | 'overheadFall';

export const POSES: PoseRigs<Poses> = {
  atk: () => {
    // A strike driven from the legs: stepped in, the hips low, the body behind the blow.
    return {
      o: 3,
      lean: 2,
      tilt: 10,
      f1: -9,
      f2: 8,
      arm: 'forward',
      far: 'back',
      face: 'atk',
      sway: 1.5,
    };
  },
  block: () => {
    // Braced for a blow: weight on the back leg, the front knee soft, leaning away a little.
    return { o: 3, lean: -1, tilt: -5, f1: -7, f2: 4, arm: 'guard', face: 'block' };
  },
  cast: () => {
    // Rooted to gather qi: a wide horse stance, upright.
    return { o: 3, f1: -5, f2: 5, arm: 'raise', face: 'cast' };
  },
  windup: () => {
    // Coiling back: the weight rocked onto the back leg, the body drawn away from the target. The
    // back foot is set where it stays through the whole blow (atk, cut, follow), so only the
    // front foot steps.
    return {
      o: 3,
      lean: -2,
      tilt: -8,
      headDx: -1,
      f1: -9,
      f2: 4,
      arm: 'windup',
      far: 'fwd',
      face: 'block',
      sway: -0.5,
    };
  },
  cut: () => {
    // The blow lands: a long step in, the front knee bent deep, the back leg pushing straight, the
    // hips carried over the front foot.
    return {
      o: 5,
      lean: 3,
      tilt: 14,
      headDx: 1,
      f1: -9,
      f2: 8,
      arm: 'cut',
      far: 'back',
      face: 'atk',
      sway: 1.5,
    };
  },
  follow: () => {
    // Carried through: still low in the lunge, the body settling over the front leg; the hair and
    // hems, trailing back through the blow, swing on past the stopped body.
    return {
      o: 4,
      lean: 2,
      tilt: 12,
      f1: -9,
      f2: 8,
      arm: 'low',
      far: 'back',
      face: 'atk',
      sway: -0.5,
    };
  },
  lowGuard: () => {
    // Crouched behind a low guard, ready to spring up, the back foot already where the thrust
    // drives from.
    return { o: 5, tilt: 10, f1: -9, f2: 5, arm: 'lowBack', far: 'fwd', face: 'block' };
  },
  thrust: () => {
    // A full bow stance (弓步): the front thigh near level, the back leg straight, body and blade
    // in one line toward the target.
    return {
      o: 6,
      lean: 3,
      tilt: 16,
      headDx: 1,
      f1: -9,
      f2: 9,
      arm: 'thrust',
      far: 'back',
      face: 'atk',
      sway: 2,
    };
  },
  overhead: () => {
    // Rising into an overhead cut: up on the back toes, the blade high; the front foot steps in
    // for the cut.
    return {
      o: 1,
      lean: -1,
      tilt: -6,
      f1: -8,
      f2: 4,
      l1: 1,
      arm: 'overhead',
      far: 'up',
      face: 'cast',
    };
  },
  chest: () => {
    // Hands together at the chest: feet together, upright and still.
    return { f1: -1, f2: 1, arm: 'chest', face: 'cast', closed: true };
  },
  blockHit: () => {
    // Driven back by a blow on the guard: skidding on the back foot, the front foot coming up,
    // the knees giving and the upper body rocked well back so the jolt reads before it settles.
    return {
      o: 4,
      lean: -3,
      tilt: -15,
      headDx: -1,
      f1: -8,
      f2: 1,
      l2: 1,
      arm: 'guard',
      face: 'block',
      sway: -1,
    };
  },
  hurt: () => {
    // Struck: the head snaps back, the body rocks away, a foot leaves the ground.
    return {
      o: 2,
      lean: -2,
      tilt: -14,
      headDx: -1,
      f1: -6,
      f2: 1,
      l2: 2,
      arm: 'flail',
      far: 'up',
      face: 'block',
      sway: -1.5,
    };
  },
  // Breakdowns (see `Poses`): the body a step ahead of the arm, the blade a step behind the hand.
  cleaveOver: () => {
    // Swinging over: the weight already rocking forward, the front foot reaching, the hand passing over the head
    // and the blade still pointing back (dragged).
    return {
      o: 2,
      lean: 0,
      tilt: 2,
      f1: -9,
      f2: 6,
      l2: 1,
      arm: 'cleaveOver',
      far: 'fwd',
      face: 'atk',
      sway: 0.5,
    };
  },
  cleaveDrive: () => {
    // Driving down into the cut: the front foot landed, the hips sinking, the wrist still cocked so the point
    // trails the hand.
    return {
      o: 4,
      lean: 3,
      tilt: 12,
      headDx: 1,
      f1: -9,
      f2: 8,
      arm: 'cleaveDrive',
      far: 'back',
      face: 'atk',
      sway: 1.5,
    };
  },
  cleaveThrough: () => {
    // Carried through and down, the body deepest in the lunge, the hair and hems still trailing.
    return {
      o: 5,
      lean: 3,
      tilt: 14,
      f1: -9,
      f2: 8,
      arm: 'cleaveThrough',
      far: 'back',
      face: 'atk',
      sway: 0.8,
    };
  },
  thrustChamber: () => {
    // Coiled: the blade drawn back level at the hip, point at the foe, the front foot lifting to step.
    return {
      o: 5,
      lean: 0,
      tilt: 6,
      f1: -9,
      f2: 6,
      l2: 1,
      arm: 'thrustChamber',
      far: 'fwd',
      face: 'atk',
      sway: 0.5,
    };
  },
  thrustRecoil: () => {
    // The point drawn back a hand's width after the thrust, the weight still forward.
    return {
      o: 5,
      lean: 2,
      tilt: 13,
      f1: -9,
      f2: 8,
      arm: 'thrustRecoil',
      far: 'back',
      face: 'atk',
      sway: 0.5,
    };
  },
  risingSweep: () => {
    // From the low guard the blade swings up past the knee, the body rising with it.
    return { o: 5, lean: 1, tilt: 12, f1: -9, f2: 7, arm: 'risingSweep', far: 'fwd', face: 'atk', sway: 1 };
  },
  risingHigh: () => {
    // Carried up past level, the body lifting out of the lunge.
    return { o: 4, lean: 2, tilt: 12, f1: -9, f2: 8, arm: 'risingHigh', far: 'back', face: 'atk', sway: 1.5 };
  },
  overheadFall: () => {
    // The blade falling from over the head, the front foot stepping in, the body sinking into the cut.
    return { o: 3, lean: 1, tilt: 4, f1: -8, f2: 6, arm: 'overheadFall', far: 'up', face: 'atk', sway: 0.5 };
  },
};

export const MOTIONS = [
  {
    id: 'cleave',
    keys: ['windup', 'cleaveOver', 'atk', 'cleaveDrive', 'cut', 'cleaveThrough', 'follow'],
    ease: ['in', 'linear', 'linear', 'linear', 'linear', 'out'],
  },
  {
    id: 'rising',
    keys: ['lowGuard', 'risingSweep', 'thrust', 'risingHigh', 'atk'],
    ease: ['in', 'linear', 'linear', 'out'],
  },
  {
    id: 'thrust',
    keys: ['lowGuard', 'thrustChamber', 'thrust', 'thrustRecoil', 'follow'],
    ease: ['inOut', 'in', 'linear', 'out'],
  },
  { id: 'overhead', keys: ['overhead', 'overheadFall', 'cut'], ease: ['in', 'linear'] },
  { id: 'chest', keys: ['ready', 'chest'], ease: ['out'] },
  { id: 'castCast', keys: ['ready', 'cast'], ease: ['out'] },
  { id: 'castOverhead', keys: ['ready', 'overhead'], ease: ['out'] },
  { id: 'castChest', keys: ['ready', 'chest'], ease: ['out'] },
  { id: 'castLowGuard', keys: ['ready', 'lowGuard'], ease: ['out'] },
  { id: 'guardUp', keys: ['ready', 'block'], ease: ['out'] },
  { id: 'guardHit', keys: ['blockHit', 'block'], ease: ['out'] },
  { id: 'hurt', keys: ['hurt', 'ready'], ease: ['inOut'] },
] as const satisfies readonly MotionDef[];

export const ARMS = {
  windup: { hand: [-5, 5], ang: -2.6, front: false },
  cut: { hand: [12, 31], ang: 0.45, front: true },
  low: { hand: [9, 37], ang: 1.1, front: true },
  lowBack: { hand: [5, 37], ang: 2.75, front: false },
  thrust: { hand: [15, 27], ang: 0, front: true },
  overhead: { hand: [8, 0], ang: Math.PI + 0.25, front: true },
  chest: { hand: [12, 29], ang: -Math.PI / 2, front: true },
  // Breakdowns (lane C0-WEAPON): each blade angle lags the hand's path between its two keys.
  cleaveOver: { hand: [3, 3], ang: -2.1, front: true },
  cleaveDrive: { hand: [14, 27], ang: -0.55, front: true },
  cleaveThrough: { hand: [11, 34], ang: 0.8, front: true },
  thrustChamber: { hand: [3, 31], ang: -0.05, front: true },
  thrustRecoil: { hand: [13, 28], ang: 0.15, front: true },
  risingSweep: { hand: [11, 34], ang: 1.0, front: true },
  risingHigh: { hand: [15, 25], ang: -0.45, front: true },
  overheadFall: { hand: [12, 12], ang: -1.3, front: true },
} satisfies Record<string, LimbSpec>;
